import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { LucideIcon } from 'lucide-react-native';
import { SPACE, FONT, RADIUS } from '@/constants/designTokens';
import { TYPO } from '@/constants/typography';

interface Props {
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  /** Emoji or short text art to display above the title */
  icon?: string;
  /** Lucide icon component (preferred over emoji icon) */
  lucideIcon?: LucideIcon;
}

/**
 * Lightweight inline empty state with optional icon.
 * Use inside scrollable areas where a full-screen EmptyState is too heavy.
 */
export default function EmptyStateInline({ title, description, actionLabel, onAction, icon, lucideIcon: LIcon }: Props) {
  const { theme } = useTheme();

  return (
    <View style={styles.container}>
      {LIcon ? <LIcon size={32} color={theme.colors.textSecondary} style={{ marginBottom: SPACE.sm }} /> : icon ? <Text style={styles.icon}>{icon}</Text> : null}
      <Text style={[styles.title, { color: theme.colors.text }]}>{title}</Text>
      {description ? (
        <Text style={[styles.description, { color: theme.colors.textSecondary }]}>
          {description}
        </Text>
      ) : null}
      {actionLabel && onAction ? (
        <TouchableOpacity
          style={[styles.button, { backgroundColor: theme.colors.accent }]}
          onPress={onAction}
          activeOpacity={0.8}
        >
          <Text style={[styles.buttonText, { color: theme.colors.background }]}>
            {actionLabel}
          </Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: SPACE['2xl'],
    paddingHorizontal: SPACE.lg,
    gap: SPACE.sm,
  },
  icon: {
    fontSize: 40,
    marginBottom: SPACE.sm,
  },
  title: {
    fontSize: FONT.md,
    fontFamily: TYPO.body.semibold,
    textAlign: 'center',
  },
  description: {
    fontSize: FONT.base,
    fontFamily: TYPO.body.regular,
    textAlign: 'center',
    lineHeight: 20,
  },
  button: {
    marginTop: SPACE.sm,
    paddingHorizontal: SPACE.md + SPACE.xs,
    paddingVertical: SPACE.sm + 2,
    borderRadius: RADIUS.xs,
  },
  buttonText: {
    fontSize: FONT.base,
    fontFamily: TYPO.body.semibold,
  },
});
